import React, { Fragment, useState, useMemo, useCallback } from 'react';
import T from 'prop-types';
import cx from 'classnames';

import {
  Menu,
  MenuItem,
  MenuGroup,
  MenuHeader,
  MenuDivider,
  MenuButton,
  useMenuProps,
  useShadowLine,
  useSearchOnMenuOpen,
  filterBySearchQuery,
} from './DropdownSet';
import SearchField from './Search/SearchField';

import { useRegions } from '../hooks';
import helpers from '../utils/helpers';

const emptyArray = [];

const CountriesDropdown = ({
  selected,
  enableWorldwide = false,
  enableRegions = false,
  excludedCountries,
  customCountries,
  customRegions,
  // Appearance
  size,
  caret = true,
  frameless = false,
  variant = 'light',
  disabled = false,
  menuGap,
  portal,
  menuAlign = 'end',
  menuDirection,
  menuWidth = 282,
  unselectedStateText = 'Select Country',
  className,
  position,
  containerClassName,
  // Handlers
  onChange,
}) => {
  const menuProps = useMenuProps({
    align: menuAlign,
    direction: menuDirection,
    position,
    ...(menuGap ? { gap: menuGap } : {}),
    ...(portal ? { portal } : {}),
    menuStyle: { width: menuWidth },
    className: cx('single-country-dropdown', className),
  });
  const [shadowLine, onScroll] = useShadowLine();
  const [searchQuery, setSearchQuery] = useState('');

  const { searchInstanceRef, onMenuChange: onSearchMenuChange } = useSearchOnMenuOpen();

  // Options
  const [regionsOptions] = useRegions(
    enableRegions ? customRegions || TINKERBELL_REGIONS : emptyArray,
    {
      excluded: ['worldwide'],
      decorateRegion: (region) => ({ value: region.value, label: region.text }),
    }
  );

  const { WW, countriesOptions } = useMemo(() => {
    let options = customCountries || helpers.mapCountries({ includeWW: enableWorldwide });

    if (excludedCountries && excludedCountries.length) {
      options = options.filter((option) => !excludedCountries.includes(option.value));
    }

    return options.reduce(
      (results, country) => {
        if (country.value === 'WW' && enableWorldwide) {
          return { ...results, WW: [country] };
        }

        return { ...results, countriesOptions: [...results.countriesOptions, country] };
      },
      { WW: [], countriesOptions: [] }
    );
  }, [customCountries, excludedCountries, enableWorldwide]);

  const menuOptions = (() => {
    const filteredWW = filterBySearchQuery(WW, searchQuery);
    const filteredCountries = filterBySearchQuery(countriesOptions, searchQuery);
    const filteredRegions = filterBySearchQuery(regionsOptions, searchQuery);

    if (enableRegions) {
      const showDividers = filteredCountries.length > 0 && filteredRegions.length > 0;

      return [
        ...filteredWW,
        ...(showDividers
          ? [
              {
                type: 'component',
                renderComponent: () => (
                  <Fragment key="regions-divider">
                    {filteredWW.length > 0 && <MenuDivider />}
                    <MenuHeader>Regions</MenuHeader>
                  </Fragment>
                ),
              },
            ]
          : []),
        ...filteredRegions,
        ...(showDividers
          ? [
              {
                type: 'component',
                renderComponent: () => (
                  <Fragment key="countries-divider">
                    <MenuDivider />
                    <MenuHeader>Countries</MenuHeader>
                  </Fragment>
                ),
              },
            ]
          : []),
        ...filteredCountries,
      ];
    }

    return [...filteredWW, ...filteredCountries];
  })();

  // Selected option
  const selectedOption =
    [...WW, ...regionsOptions, ...countriesOptions].find((option) => option.value === selected) ||
    {};

  const handleChange = useCallback(
    ({ value }) => {
      onChange(value);
    },
    [onChange]
  );

  const handleMenuChange = ({ open }) => {
    // Cleanup search field on toggle Menu
    onSearchMenuChange({ open });
  };

  return (
    <div className={cx('single-country-dropdown-container', containerClassName)}>
      <Menu
        menuButton={
          <MenuButton
            size={size}
            caret={caret}
            variant={variant}
            disabled={disabled}
            frameless={frameless}
          >
            {selectedOption.label || unselectedStateText}
          </MenuButton>
        }
        setDownOverflow
        {...menuProps}
        onMenuChange={handleMenuChange}
      >
        <SearchField instanceRef={searchInstanceRef} onChange={setSearchQuery} />
        {shadowLine}
        <MenuGroup takeOverflow onScroll={onScroll}>
          {menuOptions.map((option) => {
            if (option.type === 'component') {
              return option.renderComponent();
            }

            const { label, value, disabled: isDisabled = false } = option;
            return (
              <MenuItem
                key={value}
                value={value}
                disabled={isDisabled}
                className={cx({ active: selectedOption.value === value })}
                onClick={handleChange}
              >
                {label}
              </MenuItem>
            );
          })}
        </MenuGroup>
      </Menu>
    </div>
  );
};

CountriesDropdown.propTypes = {
  selected: T.string,
  enableWorldwide: T.bool,
  enableRegions: T.bool,
  excludedCountries: T.arrayOf(T.string),
  customCountries: T.arrayOf(
    T.shape({
      label: T.string,
      value: T.string,
      disabled: T.bool,
    })
  ),
  customRegions: T.arrayOf(
    T.shape({
      text: T.string,
      value: T.string,
      list: T.array,
    })
  ),
  // Appearance
  size: T.string,
  caret: T.bool,
  frameless: T.bool,
  variant: T.string,
  disabled: T.bool,
  menuGap: T.number,
  portal: T.bool,
  menuAlign: T.oneOf(['start', 'end']),
  menuDirection: T.string,
  menuWidth: T.number,
  unselectedStateText: T.string,
  className: T.string,
  position: T.string,
  containerClassName: T.string,
  // Handlers
  onChange: T.func.isRequired,
};

export default CountriesDropdown;
